import "./Fixture.css";

import { lazy, Suspense, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useSearchParams } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";

import { MATCH_ID, TOURNAMENT_ID } from "../../config/tournament";
import { useMediaQuery, useSoccerMatch } from "../../hooks";
import { useProgramFetch } from "../../hooks/useProgramFetch";
import { useFixtureScore } from "../../stores";
import { useAppStore } from "../../stores/useAppStore";
import { SbsAnalyticsService } from "../../utils";
import { AdUnit } from "../AdUnit";
import { DataLoader } from "../DataLoader";
import { ErrorScreen } from "../ErrorScreen";
import { FixtureBoxScore } from "../FixtureBoxScore";
import { LineUps } from "../LineUps";
import { MatchStats } from "../MatchStats";
import { Tabs, TabsItem } from "../Tabs";
import Timeline from "../Timeline/Timeline";

const AD_UNIT_PATH = "/4117/sport.sbs/fifa-world-cup-2026/match-centre";
const AD_SIZES: [number, number][] = [[300, 250], [300, 600]];

const LazyMatchStandings = lazy(() => import("../MatchStandings/MatchStandings"));
const LazyFixturesList = lazy(() => import("../FixturesList/FixturesList"));
const tabFallback = <DataLoader customStyle={{ height: "400px" }} />;

export function Fixture() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const hideMatchCentreHeader = location.pathname === "/match-centre";
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const isNarrowDesktop = useMediaQuery("(min-width: 1024px) and (max-width: 1279.99px)");

  const { match, setMatch, setMatchStatus } = useFixtureScore(
    useShallow(({ match, setMatch, setMatchStatus }) => ({
      match,
      setMatch,
      setMatchStatus,
    }))
  );

  const matchId = searchParams.get("matchId") || match?.id || MATCH_ID;
  const setShowBoxScoreOnTop = useAppStore(
    (state) => state.setShowBoxScoreOnTop
  );

  const {
    isPending,
    data: soccerMatch,
    error,
    refetch,
  } = useSoccerMatch({
    matchId: matchId || match?.id || "",
    kickoffTime: match?.matchDateTime,
  });

  const { program, isLoading: isProgramLoading } = useProgramFetch({
    matchId: matchId || soccerMatch?.id || "",
  });

  useEffect(() => {
    setShowBoxScoreOnTop(true);
  }, [setShowBoxScoreOnTop]);

  useEffect(() => {
    if (soccerMatch) {
      setMatch(soccerMatch);
      if (soccerMatch.status) setMatchStatus(soccerMatch.status);
    }
  }, [soccerMatch, setMatch, setMatchStatus]);

  if (isPending) {
    return null;
  }

  if (!matchId) {
    return (
      <ErrorScreen errorText={t("errorState.error.somethingWentWrong")} />
    );
  }

  if (error || !soccerMatch?.id) {
    return (
      <div className="fixture-box-score-error">
        <ErrorScreen refetch={refetch} />
      </div>
    );
  }

  const programDetails = {
    programId: program?.id || "",
    isLoading: isProgramLoading,
  };

  const tabs: TabsItem[] = [
    {
      label: t("tabName.timeline"),
      analyticsKey: "timeline",
      render: () => <Timeline programDetails={programDetails} />,
    },
    {
      label: t("tabName.stats"),
      analyticsKey: "stats",
      render: () => <MatchStats />,
    },
    {
      label: t("tabName.teams"),
      analyticsKey: "lineUp",
      render: () => <LineUps />,
    },
    {
      label: t("tabName.standing"),
      analyticsKey: "standing",
      render: () => (
        <Suspense fallback={tabFallback}>
          <LazyMatchStandings />
        </Suspense>
      ),
    },
    {
      label: t("tabName.fixtures"),
      analyticsKey: "fixtures",
      render: () => (
        <Suspense fallback={tabFallback}>
          <LazyFixturesList accordion />
        </Suspense>
      ),
    },
  ];

  const matchCentreHeader = (
    <div className="match-centre-header">
      <h2 className="match-centre-title">Match Centre</h2>
      <div className="match-centre-sponsor">
        <span className="match-centre-sponsor-text">Presented by</span>
        <img
          className="match-centre-sponsor-logo"
          src="/images/bet365-logo.svg"
          alt="bet365"
        />
      </div>
    </div>
  );

  const boxScoreCard = (
    <section className="fixture-section-card">
      <header className="fixture-section-header">Box Score</header>
      <div className="fixture-section-body">
        <FixtureBoxScore />
      </div>
    </section>
  );

  const timelineCard = (
    <section className="fixture-section-card">
      <header className="fixture-section-header">
        {t("tabName.timeline")}
      </header>
      <div className="fixture-section-body fixture-section-body--timeline">
        <Timeline programDetails={programDetails} />
      </div>
    </section>
  );

  const statsCard = (
    <section className="fixture-section-card">
      <header className="fixture-section-header">
        {t("tabName.statsDesktop")}
      </header>
      <div className="fixture-section-body">
        <MatchStats />
      </div>
    </section>
  );

  const standingsCard = (
    <section className="fixture-section-card">
      <header className="fixture-section-header">
        {t("tabName.standing")}
      </header>
      <div className="fixture-section-body fixture-section-body--standing">
        <Suspense fallback={tabFallback}>
          <LazyMatchStandings />
        </Suspense>
      </div>
    </section>
  );

  const lineupsCard = (
    <section className="fixture-section-card">
      <header className="fixture-section-header">
        {t("tabName.teamsDesktop")}
      </header>
      <div className="fixture-section-body fixture-section-body--lineup">
        <LineUps />
      </div>
    </section>
  );

  const fixturesCard = (
    <section className="fixture-section-card">
      <header className="fixture-section-header fixture-section-header--fixtures">
        <span>{t("tabName.fixtures")}</span>
        <div id="fixtures-select-slot" />
      </header>
      <div className="fixture-section-body fixture-section-body--fixtures">
        <Suspense fallback={tabFallback}>
          <LazyFixturesList accordion />
        </Suspense>
      </div>
    </section>
  );

  if (isNarrowDesktop) {
    return (
      <div className="fixture-details-wrapper fixture-details-wrapper--desktop fixture-details-wrapper--narrow">
        {!hideMatchCentreHeader && matchCentreHeader}
        <div className="fixture-desktop-top-area">
          <div className="fixture-details">
            <div className="fixture-desktop-top-grid">
              {boxScoreCard}
              {timelineCard}
            </div>
          </div>
          <div className="ad-desktop-rail">
            <AdUnit
              slotId="div-gpt-ad-1779839572111-0"
              adUnitPath={AD_UNIT_PATH}
              sizes={AD_SIZES}
            />
          </div>
        </div>
        <div className="fixture-desktop-bottom-grid">
          {statsCard}
          {standingsCard}
          {lineupsCard}
          {fixturesCard}
        </div>
      </div>
    );
  }

  if (isDesktop) {
    return (
      <div className="fixture-details-wrapper fixture-details-wrapper--desktop">
        {!hideMatchCentreHeader && matchCentreHeader}
        <div className="fixture-desktop-main-row">
          <div className="fixture-details">
            <div className="fixture-desktop-grid">
              {boxScoreCard}
              {timelineCard}
              <div className="fixture-column-stack--triple">
                {statsCard}
                {lineupsCard}
                {standingsCard}
              </div>
            </div>
          </div>
          <div className="ad-desktop-rail">
            <AdUnit
              slotId="div-gpt-ad-1779839572111-0"
              adUnitPath={AD_UNIT_PATH}
              sizes={AD_SIZES}
            />
            {fixturesCard}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixture-details-wrapper">
      <div className="fixture-details">
        {!hideMatchCentreHeader && matchCentreHeader}
        <div className="fixture-details-prematch-card">
          <FixtureBoxScore />
        </div>
        <div className="ad-mobile">
          <AdUnit
            slotId="div-gpt-ad-1779839572111-0"
            adUnitPath={AD_UNIT_PATH}
            sizes={AD_SIZES}
          />
        </div>
        <Tabs
          tabs={tabs}
          onChange={(_, nextItem, prevIndex) => {
            const prevItem = tabs[prevIndex];
            SbsAnalyticsService.trackEvent("tab_click", {
              match_id: matchId,
              tournament_id: TOURNAMENT_ID,
              tab_name: nextItem.analyticsKey ?? "",
              from_tab_name: prevItem?.analyticsKey ?? "",
              tab_type: nextItem.analyticsKey ?? "",
            });
          }}
        />
      </div>
    </div>
  );
}
